import { useEffect, useRef, useState } from 'react';
import api from '../services/api';

// A chat turn is expected to finish in seconds-to-tens-of-seconds, not
// minutes (contrast useProcessingStatus's 2s/30s/12min figures, sized for a
// multi-phase pipeline job) — poll snappily and cap backoff low.
const DEFAULT_POLL_INTERVAL = 1200;
const MAX_POLL_INTERVAL = 3000;
const BACKOFF_FACTOR = 1.5;
// Fires the first poll quickly rather than waiting a full interval — a
// turn that resolves in under a second (composing a follow-up with no new
// tool calls) should still get a chance to show "Thinking" before it's done.
const INITIAL_DELAY = 400;
// Hard stop well past the chat Lambda's own 180s ceiling (infrastructure/
// CLAUDE.md), so a hook whose caller forgets to flip `enabled` off can't
// poll forever.
const OVERALL_TIMEOUT = 5 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 3;

const TERMINAL_STATUSES = new Set(['done', 'error']);

/**
 * Poll GET /chat/progress/{token} while a chat turn is in flight (COL-39).
 *
 * Purely decorative: every failure mode here (a poll error, an unknown
 * token, the whole hook being disabled) degrades to "no progress shown" —
 * the actual chat turn (POST /chat) is a completely separate request this
 * hook never touches, so nothing it does can affect whether that turn
 * succeeds.
 *
 * @param {string|null} progressToken - Token minted by the caller
 *   (crypto.randomUUID()) and also passed to api.sendMessage(). null/undefined
 *   disables polling entirely.
 * @param {object} options
 * @param {boolean} options.enabled - Poll only while true (default: true).
 *   Callers should tie this to their own "is a send in flight" state so a
 *   stale token from a superseded send can't keep polling.
 * @returns {{ events: object[], status: 'idle'|'pending'|'active'|'done'|'error' }}
 */
export function useChatProgress(progressToken, options = {}) {
  const { enabled = true, pollInterval = DEFAULT_POLL_INTERVAL } = options;

  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!progressToken || !enabled) {
      setEvents([]);
      setStatus('idle');
      return;
    }

    let mounted = true;
    let timeoutId = null;
    let consecutiveErrors = 0;
    let quietPolls = 0; // consecutive polls that returned no new events
    let lastEventCount = 0;
    const startTime = Date.now();

    setEvents([]);
    setStatus('pending');

    const poll = async () => {
      if (!mounted) return;
      if (Date.now() - startTime >= OVERALL_TIMEOUT) return;

      try {
        const result = await api.getChatProgress(progressToken);
        if (!mounted) return;

        consecutiveErrors = 0;
        const nextEvents = result?.events || [];
        // Append-only on the server (shared/chat/progress.py) — a length
        // change is a cheap, sufficient "did anything happen" check without
        // deep-comparing event objects on every poll.
        if (nextEvents.length !== lastEventCount) {
          setEvents(nextEvents);
          lastEventCount = nextEvents.length;
          quietPolls = 0;
        } else {
          quietPolls++;
        }

        const nextStatus = result?.status || 'pending';
        setStatus(nextStatus);

        if (TERMINAL_STATUSES.has(nextStatus)) return; // stop polling

        const nextInterval = Math.min(
          pollInterval * Math.pow(BACKOFF_FACTOR, quietPolls),
          MAX_POLL_INTERVAL
        );
        timeoutId = setTimeout(poll, nextInterval);
      } catch {
        // A poll error is never surfaced — this hook only ever adds status
        // text on top of the existing bare-dots loading state, never
        // replaces it with an error of its own.
        if (!mounted) return;
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) return; // give up quietly
        timeoutId = setTimeout(poll, pollInterval);
      }
    };

    timeoutId = setTimeout(poll, INITIAL_DELAY);

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [progressToken, enabled, pollInterval]);

  return { events, status };
}

export default useChatProgress;
